"use client";

const BREAKPOINTS = { sm: 640, md: 900, lg: 1200, xl: 1440 };

/** 根据宽度返回断点尺寸后缀，如 "-640"、"-1440" */
function getSizeSuffix(width: number): string {
  if (width <= BREAKPOINTS.sm) return `-${BREAKPOINTS.sm}`;
  if (width <= BREAKPOINTS.md) return `-${BREAKPOINTS.md}`;
  if (width <= BREAKPOINTS.lg) return `-${BREAKPOINTS.lg}`;
  return `-${BREAKPOINTS.xl}`;
}

/** 写 vw cookie（屏幕宽度），maxAge 1 年 */
function setVwCookie(width: number) {
  document.cookie = `vw=${width};path=/;max-age=31536000;SameSite=Lax`;
}

/** 从 cookie 字符串中解析 vw 值 */
function getVwFromCookie(): number | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)vw=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * 获取适应当前设备屏幕尺寸的图片 URL
 * 命名规范: image-name-{colorMode}-{locale}-{size}.ext
 * 例如: research-cover-dark-cn-640.png
 */
export function getImageUrl(
  imageName: string,
  colorMode: string = 'light',
  locale?: string,
  sizeSensitive: boolean = false
): string {
  // 从文件名中提取基本名称和扩展名
  const lastDotIndex = imageName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `/images/${imageName}`;
  }
  
  const baseName = imageName.substring(0, lastDotIndex);
  const extension = imageName.substring(lastDotIndex);
  
  // 处理语言代码转换
  let localeSuffix;
  if (locale) {
    if (locale === 'zh-TW' || locale === 'zh-HK') {
      localeSuffix = 'tw';
    } else if (locale.startsWith('zh')) {
      localeSuffix = 'cn';
    } else {
      localeSuffix = 'en';
    }
  }
  
  // 确定分辨率后缀：优先用 window，否则用 cookie 中的 vw
  let sizeSuffix = '';
  if (sizeSensitive) {
    if (typeof window !== 'undefined') {
      sizeSuffix = getSizeSuffix(window.innerWidth);
    } else {
      const vw = getVwFromCookie();
      sizeSuffix = vw ? getSizeSuffix(vw) : `-${BREAKPOINTS.xl}`;
    }
  }
  
  let retUrl = `/images/${baseName}-${colorMode}`;
  if (localeSuffix) {
    retUrl += `-${localeSuffix}`;
  }
  if (sizeSensitive) {
    retUrl += sizeSuffix;
  }
  retUrl += extension;
  return retUrl;
}

import { useTheme } from '@mui/material';
import { useLocale } from 'next-intl';
/**
 * React Hook 版本 - 在组件中使用以响应窗口尺寸变化
 * 首次加载和 resize 时写 vw cookie，确保 SSR 时图片 URL 一致
 */
import { useState, useEffect } from 'react';

export function useResponsiveImageUrl(
  imageName: string,
  localeSensitive: boolean = true,
  sizeSensitive: boolean = true
): string {

  const locale = localeSensitive ? useLocale() : undefined;
  const theme = useTheme();
  const [imageUrl, setImageUrl] = useState<string>(getImageUrl(imageName, theme.palette.mode, locale, sizeSensitive));
  
  useEffect(() => {
    // 首次加载：写 vw cookie
    setVwCookie(window.innerWidth);
    setImageUrl(getImageUrl(imageName, theme.palette.mode, locale, sizeSensitive));
    
    const handleResize = () => {
      setVwCookie(window.innerWidth);
      setImageUrl(getImageUrl(imageName, theme.palette.mode, locale, sizeSensitive));
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageName, theme.palette.mode, locale, sizeSensitive]);
  
  return imageUrl;
}
